import { runAudioStore } from './audioIdb';
import { notifyAudioLibraryChanged } from './audioLibraryEvents';
import { EQ_PRESETS, isFlatEqGains, normalizeEqGains } from './useWebAudioEqualizer';
import { sanitizeTileColor, tileLabelForSave } from './audioTileAppearance';
import {
  cloudDeletePlaylist,
  cloudListPlaylists,
  cloudSavePlaylist,
  hasCloudAudioLibrary,
} from './audioPlayerLibraryApi';
import { deleteLocalPlaylist, mirrorPlaylistToLocal, mirrorPlaylistsToLocal } from './audioPlayerLocalMirror';

const STORE = 'playlists';

/** Removed split localStorage cache — it caused alternating playlist lists vs IndexedDB. */
const LEGACY_CACHE_IDS_KEY = 'bahlus-audio-playlist-cache-ids';
const LEGACY_CACHE_ENTRY_PREFIX = 'bahlus-audio-playlist-cache:';

let legacyCachePurged = false;

function purgeLegacyLocalStoragePlaylistCache() {
  if (legacyCachePurged || typeof localStorage === 'undefined') return;
  legacyCachePurged = true;
  try {
    const ids = JSON.parse(localStorage.getItem(LEGACY_CACHE_IDS_KEY) || '[]');
    if (Array.isArray(ids)) {
      ids.forEach((id) => {
        localStorage.removeItem(`${LEGACY_CACHE_ENTRY_PREFIX}${id}`);
      });
    }
    localStorage.removeItem(LEGACY_CACHE_IDS_KEY);
  } catch {
    localStorage.removeItem(LEGACY_CACHE_IDS_KEY);
  }
}

function sortPlaylists(rows) {
  return (rows || []).slice().sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

async function readPlaylistsFromIdb() {
  return runAudioStore(STORE, 'readonly', (store) => {
    const req = store.getAll();
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

const idbReadRetryDelayMs = 180;

async function readPlaylistsFromIdbWithRetry() {
  purgeLegacyLocalStoragePlaylistCache();
  let lastError = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return sortPlaylists(await readPlaylistsFromIdb());
    } catch (err) {
      lastError = err;
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, idbReadRetryDelayMs));
      }
    }
  }
  throw lastError || new Error('Could not read saved playlists.');
}

export function createTrackId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function trackFromStorageBoxItem(item) {
  return {
    trackId: createTrackId(),
    source: 'storagebox',
    itemId: item.itemId,
    title: item.name || 'Untitled',
    mimeType: item.mimeType || '',
  };
}

export function trackFromStreamUrl(url, title = 'Web stream') {
  return {
    trackId: createTrackId(),
    source: 'url',
    url,
    title: title || url,
    mimeType: '',
  };
}

export function playlistEqGainsFromSource(source) {
  if (source?.eqGains && typeof source.eqGains === 'object') {
    return normalizeEqGains(source.eqGains);
  }
  return normalizeEqGains(EQ_PRESETS.flat);
}

export function playlistEqGainsForSave(gains) {
  const normalized = normalizeEqGains(gains);
  return isFlatEqGains(normalized) ? null : normalized;
}

export function normalizePlaylistRecord(row) {
  if (!row?.id) return null;
  return {
    id: String(row.id),
    name: String(row.name || 'Untitled playlist'),
    tracks: Array.isArray(row.tracks) ? row.tracks : [],
    shuffle: Boolean(row.shuffle),
    repeat: Boolean(row.repeat),
    eqGains: row.eqGains && typeof row.eqGains === 'object' ? row.eqGains : null,
    tileLabel: row.tileLabel ? String(row.tileLabel) : null,
    tileColor: sanitizeTileColor(row.tileColor),
    savedAt: row.savedAt || Date.now(),
  };
}

export async function savePlaylist({ id, name, tracks, shuffle, repeat, eqGains, tileLabel, tileColor }) {
  const playlistName = String(name || 'Untitled playlist').trim() || 'Untitled playlist';
  const payload = {
    id: id || undefined,
    name: playlistName,
    tracks: Array.isArray(tracks) ? tracks : [],
    shuffle: Boolean(shuffle),
    repeat: Boolean(repeat),
    eqGains: playlistEqGainsForSave(eqGains),
    tileLabel: tileLabelForSave(tileLabel, playlistName),
    tileColor: sanitizeTileColor(tileColor),
  };

  if (await hasCloudAudioLibrary()) {
    const saved = normalizePlaylistRecord(await cloudSavePlaylist(payload));
    await mirrorPlaylistToLocal(saved);
    notifyAudioLibraryChanged({ store: 'playlists', id: saved.id, cloud: true });
    return saved;
  }

  const entry = normalizePlaylistRecord({
    ...payload,
    id: payload.id || createTrackId(),
    savedAt: Date.now(),
  });
  await runAudioStore(STORE, 'readwrite', (store) => store.put(entry));
  notifyAudioLibraryChanged({ store: 'playlists', id: entry.id });
  return entry;
}

export async function listPlaylists() {
  if (await hasCloudAudioLibrary()) {
    const rows = sortPlaylists(
      (await cloudListPlaylists()).map(normalizePlaylistRecord).filter(Boolean)
    );
    await mirrorPlaylistsToLocal(rows);
    return rows;
  }
  return sortPlaylists((await readPlaylistsFromIdbWithRetry()).map(normalizePlaylistRecord).filter(Boolean));
}

/** IndexedDB only — for migrating guest data after sign-in. */
export async function listPlaylistsLocalOnly() {
  return readPlaylistsFromIdbWithRetry();
}

export async function deletePlaylist(id) {
  if (!id) return;
  if (await hasCloudAudioLibrary()) {
    await cloudDeletePlaylist(id);
    await deleteLocalPlaylist(id);
    notifyAudioLibraryChanged({ store: 'playlists', id, deleted: true, cloud: true });
    return;
  }
  await runAudioStore(STORE, 'readwrite', (store) => store.delete(String(id)));
  notifyAudioLibraryChanged({ store: 'playlists', id, deleted: true });
}

export function resolvePlayOrder(tracksLength, playOrder) {
  if (!tracksLength) return [];
  return playOrder.length === tracksLength
    ? playOrder
    : Array.from({ length: tracksLength }, (_, i) => i);
}

/** @returns {number|null} next index in tracks array, or null when playlist ends */
export function getNextTrackIndex(tracksLength, currentIndex, playOrder, repeat) {
  if (tracksLength <= 1) return null;
  const order = resolvePlayOrder(tracksLength, playOrder);
  const idx = currentIndex >= 0 ? currentIndex : 0;
  const pos = order.indexOf(idx);
  if (pos < 0) return order[0] ?? null;
  if (pos >= order.length - 1) {
    return repeat ? order[0] : null;
  }
  return order[pos + 1];
}

export function playlistHasNextTrack(tracksLength, currentIndex, playOrder, repeat) {
  if (tracksLength <= 1) return false;
  if (repeat) return true;
  const order = resolvePlayOrder(tracksLength, playOrder);
  const idx = currentIndex >= 0 ? currentIndex : 0;
  const pos = order.indexOf(idx);
  if (pos < 0) return tracksLength > 1;
  return pos < order.length - 1;
}

export function buildShuffleOrder(length, startIndex = 0) {
  if (length <= 0) return [];
  const indices = Array.from({ length }, (_, i) => i);
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  if (startIndex >= 0 && startIndex < length) {
    const at = indices.indexOf(startIndex);
    if (at > 0) {
      const [picked] = indices.splice(at, 1);
      indices.unshift(picked);
    }
  }
  return indices;
}

export function storageBoxItemFromTrack(track) {
  if (!track || track.source !== 'storagebox') return null;
  return {
    type: 'file',
    itemId: track.itemId,
    name: track.title,
    mimeType: track.mimeType,
  };
}
